import React, { useState, useRef } from 'react';
import { useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { LectureDownloader } from '@/components/LectureDownloader';
import { 
  ArrowLeft,
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  ExternalLink, 
  FileText, 
  Maximize, 
  Minimize,
  Play, 
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Key takeaway component for highlighting important points
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6 rounded-r-md">
      <div className="flex">
        <CheckCircle2 className="h-6 w-6 text-blue-500 flex-shrink-0 mr-3 mt-1" />
        <div className="text-blue-800">{children}</div>
      </div>
    </div>
  );
};

// Scientific diagram component styled like Cell/Nature journals
const ScientificDiagram = ({ 
  title, 
  children, 
  caption,
  labelPosition = "top"
}: { 
  title: string; 
  children: React.ReactNode; 
  caption?: string;
  labelPosition?: "top" | "bottom";
}) => {
  return (
    <figure className="my-8 bg-white border rounded-md overflow-hidden shadow-sm">
      {labelPosition === "top" && (
        <div className="bg-gray-50 px-4 py-2 border-b">
          <div className="font-medium text-sm text-gray-900">Fig. {title}</div>
        </div>
      )}
      
      <div className="p-4">
        {children}
      </div>
      
      {(caption || labelPosition === "bottom") && (
        <div className="bg-gray-50 px-4 py-2 border-t">
          {labelPosition === "bottom" && (
            <div className="font-medium text-sm text-gray-900 mb-1">Fig. {title}</div>
          )}
          {caption && (
            <p className="text-xs text-gray-600 leading-snug">{caption}</p>
          )}
        </div>
      )}
    </figure>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Add fullscreen change event listener
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (slides.length === 0) return null;

  return (
    <div 
      ref={slideshowRef} 
      className={`relative overflow-hidden rounded-lg border ${isFullscreen ? 'bg-white flex flex-col h-screen' : 'bg-gray-50'}`}
    >
      {/* Slide content */}
      <div className="relative flex-grow">
        <div className={`p-6 ${isFullscreen ? 'h-[calc(100vh-70px)] overflow-y-auto' : 'max-h-[70vh] overflow-y-auto'} thin-scrollbar`}>
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-xl font-medium text-gray-900">
              {slides[currentSlide].title}
            </h3>
            <div className="flex space-x-2">
              <button 
                onClick={toggleFullscreen}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </button>
              
              {isFullscreen && (
                <button 
                  onClick={() => document.exitFullscreen()}
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
          
          <div className="prose max-w-none">
            {slides[currentSlide].content}
          </div>

          {slides[currentSlide].image && (
            <div className="flex justify-center mt-4">
              <img src={slides[currentSlide].image} alt="Slide image" className="max-w-full rounded" />
            </div>
          )}
        </div>
      </div>

      {/* Navigation controls - now always visible in fullscreen too */}
      <div className={`flex items-center justify-between p-4 border-t bg-white ${isFullscreen ? 'mt-auto' : ''}`}>
        <div className="text-sm text-gray-500">
          Slide {currentSlide + 1} of {slides.length}
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={nextSlide}
            aria-label="Next slide"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Video placeholder component
const VideoPlaceholder = () => {
  return (
    <div className="bg-gray-100 rounded-xl overflow-hidden my-10">
      <div className="aspect-video bg-gray-200 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/20"></div>
        <div className="text-center">
          <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg hover:bg-blue-700 cursor-pointer transition-colors">
            <Play className="h-8 w-8" />
          </div>
          <p className="text-gray-700 font-medium">Watch Full Lecture Video</p>
          <p className="text-sm text-gray-500 mt-1">45 minutes · Premium Content</p>
        </div>
      </div>
    </div>
  );
};

function MitochondrialHealth() {
  const [_, navigate] = useLocation();

  // Resources for the sidebar
  const resources = [
    {
      title: "Understanding Fatigue and Mitochondrial Energy Production",
      link: "#",
      icon: <FileText className="h-5 w-5 text-blue-600" />
    },
    {
      title: "Metabolomics 101 Guide",
      link: "#",
      icon: <FileText className="h-5 w-5 text-blue-600" />
    },
    {
      title: "Mitochondria and Thyroid Health",
      link: "#",
      icon: <ExternalLink className="h-5 w-5 text-blue-600" />
    },
    {
      title: "Diagnostic Tools for Mitochondrial Function",
      link: "#",
      icon: <ExternalLink className="h-5 w-5 text-blue-600" />
    }
  ];


  
  // Lecture slides data
  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "Introduction to Mitochondrial Health",
      content: (
        <>
          <p>Mitochondria are often referred to as the "powerhouses" of the cell. They generate adenosine triphosphate (ATP), the molecule that drives countless biochemical processes in living organisms. This crucial role of energy production underpins why mitochondrial health is integral to overall physiological function and longevity.</p>
          
          <p>Mitochondria go well beyond simply producing energy. They regulate cellular signaling, participate in the metabolism of lipids and amino acids, and play a fundamental role in programmed cell death (apoptosis).</p>
          
          <p>Mitochondria contain their own DNA (mtDNA), inherited almost exclusively from the maternal side. This DNA encodes essential proteins that are part of the electron transport chain and other mitochondrial processes. Because of their role in energy production, mitochondria are prolific producers of reactive oxygen species (ROS), which, if unchecked, can damage mitochondrial membranes, proteins, and even the mitochondrial DNA itself.</p>
          
          <KeyTakeaway>
            Mitochondrial health relies on a balance between efficient ATP production, minimal ROS generation, adequate antioxidant defenses (e.g., glutathione), and appropriate mitochondrial turnover (biogenesis and mitophagy).
          </KeyTakeaway>
          
          <p>Several key signaling pathways—and the nutrients and cofactors that regulate them—help maintain this equilibrium, including AMPK, mTOR, sirtuins (e.g., SIRT3), and PGC1-alpha, each playing a part in controlling energy homeostasis, antioxidant capacity, and the recycling of damaged mitochondria.</p>
        </>
      )
    },
    {
      id: 2,
      title: "Mitochondrial Structure and Function",
      content: (
        <>
          <p>Mitochondria have a distinctive double-membrane structure comprising an outer membrane and a highly folded inner membrane. The space between these membranes is the intermembrane space, while the region within the inner membrane is the matrix. The folds, or cristae, of the inner membrane greatly increase its surface area, providing ample room for the multi-protein complexes of the electron transport chain (Complexes I through IV) and ATP synthase (Complex V) to be embedded.</p>
          
          <ScientificDiagram 
            title="1. Mitochondrial Structure" 
            caption="Detailed schematic representation of mitochondrial structure showing the outer membrane, inner membrane with cristae formations, matrix, and electron transport chain complexes. Note the proximity of mtDNA to potential ROS production sites."
          >
            <div className="flex justify-center">
              <svg viewBox="0 0 800 450" className="max-w-full h-auto">
                {/* Background/Matrix */}
                <rect x="50" y="50" width="700" height="350" rx="120" fill="#FEF7E2" stroke="#D3C2AE" strokeWidth="2" />
                
                {/* Inner membrane with cristae */}
                <path d="M100 100 Q200 150 150 200 Q100 250 150 300 Q200 350 250 350 Q300 300 350 350 Q400 400 450 350 Q500 300 550 350 Q600 400 650 350 Q700 300 650 250 Q600 200 650 150 Q700 100 650 100 Q600 50 550 100 Q500 150 450 100 Q400 50 350 100 Q300 150 250 100 Q200 50 150 100 Q100 150 100 100" 
                      fill="#E6F2F8" stroke="#7AA5BF" strokeWidth="3" />
                
                {/* Matrix granules */}
                <circle cx="200" cy="200" r="10" fill="#BED8E5" />
                <circle cx="300" cy="250" r="8" fill="#BED8E5" />
                <circle cx="400" cy="180" r="12" fill="#BED8E5" />
                <circle cx="500" cy="270" r="9" fill="#BED8E5" />
                <circle cx="600" cy="220" r="11" fill="#BED8E5" />
                
                {/* mtDNA */}
                <circle cx="350" cy="200" r="25" fill="none" stroke="#E06C75" strokeWidth="3" strokeDasharray="5,3" />
                <text x="330" y="205" fill="#E06C75" fontFamily="sans-serif" fontSize="14" fontWeight="bold">mtDNA</text>
                
                {/* ETC Complexes */}
                <g transform="translate(200, 325)">
                  <rect x="0" y="0" width="30" height="40" rx="5" fill="#4078b8" />
                  <text x="15" y="55" fill="#333" fontFamily="sans-serif" fontSize="12" textAnchor="middle">I</text>
                </g>
                
                <g transform="translate(300, 325)">
                  <rect x="0" y="0" width="30" height="40" rx="5" fill="#50a050" />
                  <text x="15" y="55" fill="#333" fontFamily="sans-serif" fontSize="12" textAnchor="middle">II</text>
                </g>
                
                <g transform="translate(400, 325)">
                  <rect x="0" y="0" width="30" height="40" rx="5" fill="#c0392b" />
                  <text x="15" y="55" fill="#333" fontFamily="sans-serif" fontSize="12" textAnchor="middle">III</text>
                </g>
                
                <g transform="translate(500, 325)">
                  <rect x="0" y="0" width="30" height="40" rx="5" fill="#9b59b6" />
                  <text x="15" y="55" fill="#333" fontFamily="sans-serif" fontSize="12" textAnchor="middle">IV</text>
                </g>
                
                <g transform="translate(600, 325)">
                  <rect x="0" y="0" width="30" height="40" rx="5" fill="#f1c40f" />
                  <text x="15" y="55" fill="#333" fontFamily="sans-serif" fontSize="12" textAnchor="middle">V</text>
                </g>
                
                {/* Labels */}
                <text x="400" y="40" fill="#333" fontFamily="sans-serif" fontSize="16" fontWeight="bold" textAnchor="middle">Outer Membrane</text>
                <text x="400" y="80" fill="#333" fontFamily="sans-serif" fontSize="16" fontWeight="bold" textAnchor="middle">Intermembrane Space</text>
                <text x="400" y="170" fill="#333" fontFamily="sans-serif" fontSize="16" fontWeight="bold" textAnchor="middle">Matrix</text>
                <text x="400" y="315" fill="#333" fontFamily="sans-serif" fontSize="14" fontWeight="bold" textAnchor="middle">Inner Membrane (Cristae)</text>
                <text x="400" y="395" fill="#333" fontFamily="sans-serif" fontSize="14" fontWeight="bold" textAnchor="middle">Electron Transport Chain Complexes</text>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="bg-gray-100 p-5 rounded-lg my-6">
            <h3 className="text-lg font-medium mb-3">Key Functions of Mitochondria</h3>
            <ul className="space-y-2">
              <li><strong>ATP Production:</strong> At the core of mitochondrial function is the synthesis of ATP via oxidative phosphorylation.</li>
              <li><strong>Reactive Oxygen Species Generation & Detoxification:</strong> While mitochondria produce ROS as a byproduct of aerobic respiration, they also contain intrinsic enzymatic and non-enzymatic antioxidants.</li>
              <li><strong>Regulation of Apoptosis:</strong> Mitochondria can release cytochrome c in response to cellular damage or stress, activating a cascade of events leading to programmed cell death.</li>
              <li><strong>Intermediate Metabolism:</strong> Mitochondria host enzymes involved in the beta-oxidation of fatty acids, ketogenesis, certain steps of the urea cycle, and amino acid metabolism.</li>
              <li><strong>Storage of Calcium:</strong> The mitochondrial matrix can temporarily store calcium ions (Ca2+), contributing to intracellular Ca2+ homeostasis and cell signaling.</li>
            </ul>
          </div>
          
          <KeyTakeaway>
            When these processes become unbalanced—due to mitochondrial DNA mutations, loss of membrane integrity, or depletion of cofactors like magnesium and CoQ10—cells are less able to generate ATP efficiently, leading to energy deficits, increased ROS, and further mitochondrial damage.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 3,
      title: "Key Metabolic Pathways",
      content: (
        <>
          <h3 className="text-xl font-medium mb-4">A. Glycolysis</h3>
          <p>Glycolysis occurs in the cytosol and is the first step in carbohydrate metabolism. Glucose (a six-carbon sugar) is split into two molecules of pyruvate. This process generates a small net yield of ATP and the reduced form of the electron carrier NADH. If oxygen is sufficient and mitochondria function optimally, pyruvate is imported into mitochondria and converted to acetyl-CoA, fueling the citric acid cycle.</p>
          
          <h3 className="text-xl font-medium mb-4 mt-6">B. Citric Acid Cycle (Krebs Cycle)</h3>
          <p>Within the mitochondrial matrix, acetyl-CoA condenses with oxaloacetate to form citrate, which undergoes a series of reactions that continually regenerate oxaloacetate. The cycle harvests electrons and protons, storing them in NADH and FADH2. These carriers then donate electrons to Complexes I and II of the electron transport chain, respectively.</p>
          
          <ScientificDiagram 
            title="2. Integrated Metabolic Pathways in Mitochondria" 
            caption="Schematic representation of the major metabolic pathways involved in mitochondrial energy production, showing relationships between glycolysis, fatty acid oxidation, citric acid cycle, and the electron transport chain. Note the convergence of different nutrient sources at the citric acid cycle."
          >
            <div className="flex justify-center">
              <svg viewBox="0 0 900 500" className="max-w-full h-auto">
                {/* Background cell compartments */}
                <rect x="0" y="0" width="900" height="500" fill="#fafafa" />
                <rect x="0" y="0" width="900" height="250" fill="#f5f5f5" stroke="#ddd" strokeWidth="2" />
                <rect x="0" y="250" width="900" height="250" fill="#fff4e6" stroke="#ddd" strokeWidth="2" />
                
                {/* Cell compartment labels */}
                <text x="30" y="30" fontSize="16" fontWeight="bold" fill="#555">CYTOSOL</text>
                <text x="30" y="280" fontSize="16" fontWeight="bold" fill="#555">MITOCHONDRION</text>
                
                {/* Glycolysis pathway */}
                <rect x="100" y="80" width="120" height="60" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2" />
                <text x="160" y="115" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1565c0">Glucose</text>
                
                <path d="M160 140 L160 180" stroke="#2196f3" strokeWidth="3" strokeDasharray="5,3" />
                <polygon points="155,175 160,190 165,175" fill="#2196f3" />
                
                <rect x="100" y="190" width="120" height="60" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2" />
                <text x="160" y="220" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1565c0">Pyruvate</text>
                <text x="160" y="240" textAnchor="middle" fontSize="12" fill="#1565c0">(2 ATP, 2 NADH)</text>
                
                {/* Pyruvate to Acetyl-CoA */}
                <path d="M220 220 L280 220 L280 320" stroke="#2196f3" strokeWidth="3" />
                <polygon points="275,315 280,330 285,315" fill="#2196f3" />
                <text x="250" y="210" textAnchor="middle" fontSize="12" fill="#666">PDH</text>
                
                {/* Fatty acid pathway */}
                <rect x="400" y="80" width="120" height="60" rx="10" fill="#f3e5f5" stroke="#9c27b0" strokeWidth="2" />
                <text x="460" y="115" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#6a1b9a">Fatty Acids</text>
                
                <path d="M460 140 L460 320" stroke="#9c27b0" strokeWidth="3" strokeDasharray="5,3" />
                <polygon points="455,315 460,330 465,315" fill="#9c27b0" />
                <text x="480" y="200" textAnchor="middle" fontSize="12" fill="#666">β-oxidation</text>
                
                {/* Acetyl-CoA converging point */}
                <circle cx="350" cy="350" r="50" fill="#fff8e1" stroke="#ffa000" strokeWidth="3" />
                <text x="350" y="340" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#e65100">Acetyl-CoA</text>
                <text x="350" y="365" textAnchor="middle" fontSize="14" fill="#e65100">Entry Point</text>
                
                {/* Citric Acid Cycle */}
                <ellipse cx="550" cy="350" rx="80" ry="70" fill="#e8f5e9" stroke="#4caf50" strokeWidth="3" />
                <text x="550" y="335" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#2e7d32">Citric Acid</text>
                <text x="550" y="355" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#2e7d32">Cycle</text>
                <text x="550" y="375" textAnchor="middle" fontSize="12" fill="#2e7d32">(3 NADH, 1 FADH2, 1 GTP)</text>
                
                {/* Connection Acetyl-CoA to TCA */}
                <path d="M400 350 L470 350" stroke="#ff9800" strokeWidth="3" />
                <polygon points="465,345 480,350 465,355" fill="#ff9800" />
                
                {/* ETC and ATP synthase */}
                <rect x="700" y="300" width="150" height="100" rx="10" fill="#ffebee" stroke="#f44336" strokeWidth="3" />
                <text x="775" y="335" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#c62828">Electron</text>
                <text x="775" y="355" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#c62828">Transport</text>
                <text x="775" y="375" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#c62828">Chain</text>
                <text x="775" y="395" textAnchor="middle" fontSize="12" fill="#c62828">(32-34 ATP)</text>
                
                {/* Connection TCA to ETC */}
                <path d="M630 350 L700 350" stroke="#4caf50" strokeWidth="3" />
                <polygon points="695,345 710,350 695,355" fill="#4caf50" />
                <text x="650" y="340" textAnchor="middle" fontSize="12" fill="#666">NADH, FADH2</text>
                
                {/* O2 input */}
                <path d="M775 250 L775 300" stroke="#78909c" strokeWidth="2" strokeDasharray="5,3" />
                <polygon points="770,295 775,310 780,295" fill="#78909c" />
                <text x="775" y="240" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#37474f">O₂</text>
                
                {/* H2O output */}
                <path d="M775 400 L775 450" stroke="#78909c" strokeWidth="2" strokeDasharray="5,3" />
                <polygon points="770,445 775,460 780,445" fill="#78909c" />
                <text x="775" y="480" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#37474f">H₂O</text>
                
                {/* ATP output */}
                <path d="M850 350 L880 350" stroke="#f44336" strokeWidth="3" />
                <circle cx="900" cy="350" r="20" fill="#fff3e0" stroke="#ff9800" strokeWidth="2" />
                <text x="900" y="355" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#ff6f00">ATP</text>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h4 className="font-medium text-blue-900 mb-2">C. Fatty Acid Oxidation</h4>
              <p>Fatty acids, sourced from lipids, undergo beta-oxidation in the mitochondrial matrix. This process progressively cleaves fatty acid chains two carbons at a time, forming multiple acetyl-CoA units that feed into the citric acid cycle.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h4 className="font-medium text-indigo-900 mb-2">D. Electron Transport Chain (ETC)</h4>
              <p>Electrons from NADH and FADH2 traverse a series of complexes in the inner mitochondrial membrane. With each transfer, protons are pumped into the intermembrane space, generating an electrochemical proton gradient.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            These interconnected pathways form the basis of cellular energy production, and their disruption underlies many clinical presentations of fatigue, exercise intolerance, and metabolic disorders.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 4,
      title: "Regulation and Signaling Pathways",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">A. AMPK</h3>
              <p>AMPK acts as a master energy regulator, sensitive to the AMP/ATP ratio in the cell. When ATP levels fall and AMP levels rise (signaling energy stress), AMPK is activated. Once active, it promotes catabolic pathways and inhibits anabolic processes.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">B. mTOR</h3>
              <p>mTOR is a protein kinase that senses nutrient abundance, especially amino acids. It promotes cell growth and anabolic processes—increasing protein synthesis, lipid synthesis, and ribosome biogenesis.</p>
            </div>
          </div>
          
          <ScientificDiagram 
            title="3. Mitochondrial Signaling Networks" 
            caption="Key regulatory pathways that control mitochondrial function. AMPK (energy sensor) inhibits mTOR (growth regulator) while activating PGC1-α (biogenesis factor). SIRT3 reduces ROS production and activates PGC1-α when NAD+ levels are high."
          >
            <div className="flex justify-center">
              <svg viewBox="0 0 800 400" className="max-w-full h-auto">
                {/* Background */}
                <rect x="0" y="0" width="800" height="400" fill="#f8f9fa" />
                
                {/* Central mitochondrion */}
                <ellipse cx="400" cy="200" rx="80" ry="50" fill="#fff4e6" stroke="#fd7e14" strokeWidth="3" />
                <text x="400" y="205" textAnchor="middle" fontWeight="bold" fill="#e67700">Mitochondrion</text>
                
                {/* Key regulators */}
                {/* AMPK */}
                <circle cx="200" cy="100" r="60" fill="#e7f5ff" stroke="#1864ab" strokeWidth="3" />
                <text x="200" y="90" textAnchor="middle" fontWeight="bold" fontSize="24" fill="#1864ab">AMPK</text>
                <text x="200" y="115" textAnchor="middle" fontSize="14" fill="#1864ab">Energy Sensor</text>
                
                {/* mTOR */}
                <circle cx="600" cy="100" r="60" fill="#fff9db" stroke="#e67700" strokeWidth="3" />
                <text x="600" y="90" textAnchor="middle" fontWeight="bold" fontSize="24" fill="#e67700">mTOR</text>
                <text x="600" y="115" textAnchor="middle" fontSize="14" fill="#e67700">Growth Factor</text>
                
                {/* PGC1-α */}
                <circle cx="400" cy="320" r="60" fill="#ebfbee" stroke="#2b8a3e" strokeWidth="3" />
                <text x="400" y="310" textAnchor="middle" fontWeight="bold" fontSize="24" fill="#2b8a3e">PGC1-α</text>
                <text x="400" y="335" textAnchor="middle" fontSize="14" fill="#2b8a3e">Biogenesis</text>
                
                {/* SIRT3 - removed from diagram to reduce complexity */}
                
                {/* Key pathways - simplified */}
                {/* AMPK inhibits mTOR */}
                <path d="M260 100 L540 100" stroke="#fa5252" strokeWidth="4" strokeDasharray="10,5" />
                <circle cx="400" cy="100" r="20" fill="white" stroke="#fa5252" strokeWidth="3" />
                <text x="400" y="108" textAnchor="middle" fontSize="24" fontWeight="bold" fill="#fa5252">⊣</text>
                
                {/* AMPK activates PGC1-α */}
                <path d="M230 155 L350 280" stroke="#2b8a3e" strokeWidth="4" />
                <polygon points="345,270 360,290 335,290" fill="#2b8a3e" />
                <text x="265" y="230" textAnchor="middle" fontSize="16" fill="#2b8a3e">Activates</text>
                
                {/* mTOR connection to PGC1-α (context-dependent) */}
                <path d="M570 155 L450 280" stroke="#fd7e14" strokeWidth="4" strokeDasharray="8,4" />
                <polygon points="455,270 440,290 465,290" fill="#fd7e14" />
                <text x="520" y="230" textAnchor="middle" fontSize="16" fill="#fd7e14">Context-dependent</text>
                
                {/* Effects on mitochondria */}
                <path d="M380 260 L380 230" stroke="#2b8a3e" strokeWidth="3" />
                <polygon points="375,240 380,225 385,240" fill="#2b8a3e" />
                <text x="330" y="245" textAnchor="end" fontSize="14" fill="#2b8a3e">↑ New Mitochondria</text>
                
                {/* Environmental cues */}
                <text x="120" y="60" textAnchor="middle" fontSize="14" fill="#495057">Low Energy</text>
                <text x="120" y="75" textAnchor="middle" fontSize="14" fill="#495057">Exercise</text>
                <path d="M140 80 L170 90" stroke="#495057" strokeWidth="2" strokeDasharray="5,3" />
                
                <text x="680" y="60" textAnchor="middle" fontSize="14" fill="#495057">Amino Acids</text>
                <text x="680" y="75" textAnchor="middle" fontSize="14" fill="#495057">Growth Factors</text>
                <path d="M660 80 L630 90" stroke="#495057" strokeWidth="2" strokeDasharray="5,3" />
                
                {/* Title */}
                <text x="400" y="25" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#343a40">Key Mitochondrial Signaling Pathways</text>
              </svg>
            </div>
          </ScientificDiagram>
          
          <p>Of note, amino acid supplementation can selectively support mTOR-related pathways to build or repair muscle tissue and encourage mitochondrial biogenesis when used judiciously. For instance, supplementing with crystalline free-form amino acids (e.g., two teaspoons once or twice daily) can support muscle recovery, especially in cases of elevated energy demands or significant protein-catabolism states.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">C. Sirtuins (especially SIRT3)</h3>
              <p>Sirtuins are NAD+-dependent deacetylases that modulate mitochondrial function, stress resistance, and metabolic efficiency. SIRT3 localizes to the mitochondria, where it deacetylates and activates various enzymes crucial for energy production and antioxidant defenses.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">D. PGC1-alpha</h3>
              <p>PGC1-alpha is a coactivator that works with transcription factors to upregulate genes involved in mitochondrial biogenesis, oxidative phosphorylation, and fatty acid oxidation.</p>
            </div>
          </div>
          
          <h3 className="text-xl font-medium mb-3">E. Mitochondrial Dynamics</h3>
          <p>Mitochondria are constantly remodeled through fusion and fission processes—collectively referred to as mitochondrial dynamics. Fusion helps mitigate damage by mixing partially dysfunctional mitochondria with healthier ones, diluting defective components. Fission creates new mitochondria and also segregates damaged mitochondrial fragments, which can then be eliminated by mitophagy.</p>
        </>
      )
    },
    {
      id: 5,
      title: "Mechanisms of Mitochondrial Dysfunction",
      content: (
        <>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-medium mb-3">A. Oxidative Stress and ROS</h3>
              <p>Although controlled ROS levels can act as signaling molecules, excessive ROS are toxic. They can damage lipids within the mitochondrial membrane (leading to lipid peroxidation), damage proteins (causing misfolding or inactivation), and mutate mitochondrial DNA (mtDNA).</p>
            </div>
            
            <ScientificDiagram 
              title="4. Vicious Cycle of Mitochondrial Dysfunction" 
              caption="The self-perpetuating cycle of mitochondrial damage. Primary insults (toxins, inflammation, nutrient deficiencies) lead to ROS production and oxidative damage, which further compromises electron transport chain efficiency, creating a feedback loop of increasing dysfunction. This process underlies many chronic diseases associated with aging."
            >
              <div className="flex justify-center">
                <svg viewBox="0 0 800 500" className="max-w-full h-auto">
                  {/* Background */}
                  <rect x="0" y="0" width="800" height="500" fill="#f8f9fa" />
                  
                  {/* Mitochondrion outline */}
                  <ellipse cx="400" cy="250" rx="350" ry="200" fill="#fff4e6" stroke="#fd7e14" strokeWidth="2" strokeDasharray="5,3" />
                  
                  {/* Central dysfunction circle */}
                  <circle cx="400" cy="250" r="120" fill="#fff1f0" stroke="#f03e3e" strokeWidth="3" />
                  <text x="400" y="240" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#c92a2a">Mitochondrial</text>
                  <text x="400" y="265" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#c92a2a">Dysfunction</text>
                  
                  {/* Cycle components */}
                  {/* 1. Initial insults */}
                  <circle cx="200" cy="100" r="70" fill="#e7f5ff" stroke="#1971c2" strokeWidth="2" />
                  <text x="200" y="90" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1971c2">Primary Insults</text>
                  <text x="200" y="115" textAnchor="middle" fontSize="12" fill="#1971c2">Toxins, Medications</text>
                  <text x="200" y="130" textAnchor="middle" fontSize="12" fill="#1971c2">Nutrient Deficiencies</text>
                  
                  {/* 2. ETC Dysfunction */}
                  <circle cx="600" cy="100" r="70" fill="#fff4e6" stroke="#e67700" strokeWidth="2" />
                  <text x="600" y="90" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#e67700">ETC Dysfunction</text>
                  <text x="600" y="115" textAnchor="middle" fontSize="12" fill="#e67700">↓ ATP Production</text>
                  <text x="600" y="130" textAnchor="middle" fontSize="12" fill="#e67700">Electron Leak</text>
                  
                  {/* 3. ROS Generation */}
                  <circle cx="600" cy="400" r="70" fill="#fff9db" stroke="#f08c00" strokeWidth="2" />
                  <text x="600" y="390" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#f08c00">ROS Generation</text>
                  <text x="600" y="415" textAnchor="middle" fontSize="12" fill="#f08c00">O₂⁻•, H₂O₂, •OH</text>
                  <text x="600" y="430" textAnchor="middle" fontSize="12" fill="#f08c00">Oxidative Stress</text>
                  
                  {/* 4. Oxidative Damage */}
                  <circle cx="200" cy="400" r="70" fill="#f8f0fc" stroke="#ae3ec9" strokeWidth="2" />
                  <text x="200" y="390" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#ae3ec9">Oxidative Damage</text>
                  <text x="200" y="415" textAnchor="middle" fontSize="12" fill="#ae3ec9">mtDNA Mutations</text>
                  <text x="200" y="430" textAnchor="middle" fontSize="12" fill="#ae3ec9">Lipid Peroxidation</text>
                  
                  {/* Arrows for cycle */}
                  {/* Primary Insults to ETC Dysfunction */}
                  <path d="M270 100 L530 100" stroke="#1971c2" strokeWidth="3" />
                  <polygon points="525,95 540,100 525,105" fill="#1971c2" />
                  
                  {/* ETC Dysfunction to ROS */}
                  <path d="M600 170 L600 330" stroke="#e67700" strokeWidth="3" />
                  <polygon points="595,325 600,340 605,325" fill="#e67700" />
                  
                  {/* ROS to Oxidative Damage */}
                  <path d="M530 400 L270 400" stroke="#f08c00" strokeWidth="3" />
                  <polygon points="275,395 260,400 275,405" fill="#f08c00" />
                  
                  {/* Oxidative Damage to Primary Insults (completing the cycle) */}
                  <path d="M200 330 L200 170" stroke="#ae3ec9" strokeWidth="3" />
                  <polygon points="195,175 200,160 205,175" fill="#ae3ec9" />
                  
                  {/* Additional connections to central dysfunction */}
                  <path d="M270 130 L320 190" stroke="#1971c2" strokeWidth="2" strokeDasharray="5,3" />
                  <path d="M530 130 L480 190" stroke="#e67700" strokeWidth="2" strokeDasharray="5,3" />
                  <path d="M530 370 L480 310" stroke="#f08c00" strokeWidth="2" strokeDasharray="5,3" />
                  <path d="M270 370 L320 310" stroke="#ae3ec9" strokeWidth="2" strokeDasharray="5,3" />
                  
                  {/* Legend */}
                  <text x="400" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#343a40">The Vicious Cycle of Mitochondrial Dysfunction</text>
                  
                  {/* Disease outcomes */}
                  <text x="80" y="280" textAnchor="middle" fontSize="14" fill="#495057" transform="rotate(-90, 80, 280)">Chronic Fatigue</text>
                  <text x="720" y="280" textAnchor="middle" fontSize="14" fill="#495057" transform="rotate(90, 720, 280)">Neurodegeneration</text>
                  <text x="400" y="480" textAnchor="middle" fontSize="14" fill="#495057">Accelerated Aging</text>
                </svg>
              </div>
            </ScientificDiagram>
            
            <div>
              <h3 className="text-xl font-medium mb-3">B. Toxins and Medications</h3>
              <p>Environmental toxins (heavy metals, pesticides), industrial chemicals, and certain pharmaceuticals can impair mitochondrial enzymes or the integrity of membranes. For instance, some antidepressants have been implicated in mitochondrial toxicity by disrupting Complexes I and IV.</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">C. Nutrient Depletion</h3>
              <p>Nutrients such as CoQ10 (a key electron carrier), magnesium (a cofactor in hundreds of enzymatic reactions, including ATP production), B vitamins (crucial for NADH and FADH2 generation), and antioxidants like glutathione are essential for mitochondrial health.</p>
              
              <KeyTakeaway>
                When boosting mitochondrial function—particularly via supplementation with magnesium—clinicians must evaluate whether glutathione levels are sufficient. If magnesium therapy fails to provide measurable improvements in energy production, underlying glutathione depletion may be a limiting factor.
              </KeyTakeaway>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">D. Mitochondrial DNA Mutations</h3>
              <p>Unlike nuclear DNA, mitochondrial DNA has fewer protective histones and is exposed to higher concentrations of ROS. Over a lifetime of oxidative stress, mtDNA accumulates mutations that reduce the efficiency of the electron transport chain.</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">E. Inflammatory Signaling</h3>
              <p>Chronic low-grade inflammation, often due to metabolic syndrome, obesity, gut dysbiosis, or autoimmune activity, can directly impair mitochondrial function. Pro-inflammatory cytokines may disrupt mitochondrial processes or alter the redox status of the cell.</p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 6,
      title: "Disease States Implicating Mitochondrial Pathways",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">A. Chronic Fatigue and Fibromyalgia</h3>
              <p>A prominent cause of fatigue is the inability of mitochondria to produce adequate ATP. Patients may present with muscle weakness, exercise intolerance, and non-restorative sleep.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">B. Cardiovascular Disease</h3>
              <p>Cardiac cells have a high density of mitochondria due to the heart's continuous demand for ATP. Dysfunctional mitochondria contribute to poor cardiac contractility, arrhythmias, and heart failure progression.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">C. Metabolic Syndrome and Diabetes</h3>
              <p>The interplay between insulin signaling, fatty acid oxidation, and mitochondrial function is central to metabolic health. In insulin resistance, cells struggle to effectively utilize glucose.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">D. Neurodegenerative Disorders</h3>
              <p>The brain, despite comprising only approximately 2% of body weight, consumes about 20% of resting metabolic oxygen. This intense energy demand makes neurons exceedingly sensitive to mitochondrial dysfunction.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="font-medium text-amber-900 mb-2">E. Cancer</h3>
              <p>Rapidly proliferating cancer cells have altered metabolic needs, and many rewire their mitochondrial (and glycolytic) pathways accordingly. The Warburg effect—where cancer cells opt for glycolysis over oxidative phosphorylation, even in the presence of oxygen.</p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-md">
              <h3 className="font-medium text-emerald-900 mb-2">F. Aging</h3>
              <p>Aging is intricately linked to cumulative mitochondrial damage. With time, the efficiency of the electron transport chain may decline, generating more ROS and further damaging mtDNA.</p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 7,
      title: "Compounds and Nutrients Influencing Mitochondrial Pathways",
      content: (
        <>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-medium mb-3">A. CoQ10 (Ubiquinone)</h3>
              <p>CoQ10 is a lipid-soluble antioxidant and a key electron carrier within the ETC. It shuttles electrons from Complexes I and II to Complex III, enabling proton pumping. Supplementation with CoQ10 often supports cardiac function, reduces fatigue, and can improve outcomes in neurodegenerative conditions.</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">B. Magnesium</h3>
              <p>Magnesium is involved in hundreds of enzymatic reactions, including those critical for ATP synthesis. Each molecule of ATP exists in complex with magnesium to be biochemically active. Furthermore, vitamin B6 supplementation often enhances magnesium utilization and can help ensure that magnesium-based interventions yield maximal benefit.</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">C. Glutathione</h3>
              <p>Glutathione is often called the "master antioxidant." Beyond neutralizing free radicals and peroxides, it maintains other antioxidants like vitamin C and vitamin E in active forms. Glutathione is also vital for detoxification pathways in the liver.</p>
              
              <KeyTakeaway>
                Poor clinical response to magnesium therapy often indicates insufficient glutathione stores. Monitoring glutathione status can be critical, as low glutathione correlates with persistent fatigue.
              </KeyTakeaway>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">D. NAC (N-acetylcysteine)</h3>
              <p>NAC provides cysteine for glutathione synthesis. It is known for its mucolytic benefits, but its most fundamental role involves bolstering cellular defenses against oxidative injury. NAC helps restore intracellular glutathione, supporting detoxification of heavy metals and countering toxin-induced mitochondrial damage.</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">E. B Vitamins</h3>
              <p>Several B vitamins function as cofactors or precursors to electron carriers necessary for cellular energy production:</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>Vitamin B3 (niacin) is required for NAD+ formation</li>
                <li>Vitamin B2 (riboflavin) for FAD/FADH2</li>
                <li>Vitamin B1 (thiamine) for the pyruvate dehydrogenase complex</li>
                <li>Vitamin B12 and folate are crucial for methylation pathways</li>
                <li>Vitamin B6 (pyridoxine) supports numerous enzymatic processes</li>
              </ul>
            </div>
          </div>
        </>
      )
    },
    {
      id: 8,
      title: "Amino Acids and Specialized Compounds",
      content: (
        <>
          <h3 className="text-xl font-medium mb-3">F. Amino Acids</h3>
          <div className="space-y-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h4 className="font-medium text-blue-900 mb-1">Tryptophan</h4>
              <p>Commonly depleted in individuals with chronic stress or poor dietary protein intake. Supplementation can help restore neurotransmitter balance (e.g., serotonin) and support general anabolic processes.</p>
            </div>
            
            <div className="bg-indigo-50 p-4 rounded-md">
              <h4 className="font-medium text-indigo-900 mb-1">Carnitine</h4>
              <p>Necessary for transporting long-chain fatty acids into the mitochondria for beta-oxidation. If laboratory markers suggest elevated fatty acid metabolites and incomplete oxidation, carnitine supplementation may lift the bottleneck.</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-md">
              <h4 className="font-medium text-purple-900 mb-1">Branched-Chain Amino Acids (BCAAs)</h4>
              <p>These can be strategic for activating mTOR pathways in cases where muscle repair or mitochondrial biogenesis is a priority. They should be used judiciously to avoid overstimulation of growth pathways but can be indispensable in catabolic states.</p>
            </div>
          </div>
          
          <h3 className="text-xl font-medium mb-3">G. SS31 Peptide (Elamipretide)</h3>
          <p>Recent research has identified mitochondrial-targeted peptides such as SS31 (also known as Elamipretide). SS31 can bind to cardiolipin, a unique phospholipid found in the inner mitochondrial membrane, stabilizing it and reducing ROS production at the source. Studies suggest that SS31 treatment may enhance electron transport efficiency, reduce oxidative damage, and improve ATP output.</p>
          
          <h3 className="text-xl font-medium mb-3 mt-6">H. Metformin and GLP-1 Agonists</h3>
          <p>Drugs such as metformin, widely used for type 2 diabetes, partially inhibit Complex I of the ETC, activating AMPK in the process. This reduces hepatic gluconeogenesis, lowers blood glucose, and can improve insulin sensitivity. GLP-1 agonists, meanwhile, enhance insulin secretion in a glucose-dependent manner and may improve metabolic efficiency, sometimes influencing mitochondrial function beneficially.</p>
        </>
      )
    },
    {
      id: 9,
      title: "Dietary and Lifestyle Interventions",
      content: (
        <>
          <p>In addition to supplementation, lifestyle interventions are key to mitochondrial health:</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Regular Exercise</h3>
              <p>Endurance and high-intensity interval training upregulate PGC1-alpha, driving mitochondrial biogenesis and improving oxidative capacity.</p>
            </div>
            
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Intermittent Fasting</h3>
              <p>Stimulates AMPK, promotes autophagy/mitophagy to clear damaged mitochondria, and can maintain a healthy NAD+/NADH ratio, supporting sirtuin activity.</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Ketogenic Diet</h3>
              <p>Shifts metabolism toward fatty acid oxidation and ketone utilization, altering mitochondrial and cellular energetic states.</p>
            </div>
            
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Adequate Dietary Fat</h3>
              <p>Ensures proper formation of mitochondrial membranes and supports absorption of fat-soluble nutrients such as CoQ10.</p>
            </div>
            
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="font-medium text-amber-900 mb-2">Stress Reduction</h3>
              <p>Chronic stress and sleep deprivation elevate cortisol, disturb circadian rhythms, and contribute to ROS build-up, all of which can compromise mitochondrial functions.</p>
            </div>
            
            <div className="bg-emerald-50 p-4 rounded-md">
              <h3 className="font-medium text-emerald-900 mb-2">Emotional Wellness</h3>
              <p>Prioritizing fulfilling relationships and emotional wellness also supports overall metabolic and immune balance.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            A comprehensive approach to mitochondrial health incorporates both targeted supplementation and holistic lifestyle modifications designed to optimize energy production and minimize oxidative damage.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 10,
      title: "Diagnostic Tools for Assessing Mitochondrial Health",
      content: (
        <>
          <p>Clinicians often use several strategies to gauge mitochondrial function:</p>
          
          <div className="space-y-4 my-6">
            <div className="bg-gray-100 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 mb-2">Organic Acids Testing (OAT)</h3>
              <p>Measures metabolites in urine that reflect mitochondrial function or dysfunction (e.g., elevated lactate can indicate inadequate aerobic metabolism).</p>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 mb-2">Blood Biomarkers</h3>
              <p>Lactate, pyruvate, creatine kinase, and inflammatory markers like C-reactive protein can offer indirect clues. RBC magnesium levels may better represent intracellular magnesium status than serum magnesium alone. Monitoring glutathione status can also be critical, as low glutathione correlates with persistent fatigue.</p>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 mb-2">Oxidative Stress Markers</h3>
              <p>Oxidized LDL, 8-hydroxy-2'-deoxyguanosine (8-OHdG), and DNA adducts can provide insight into the degree of cellular and mitochondrial oxidative damage.</p>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 mb-2">Genetic Testing</h3>
              <p>mtDNA sequences can be evaluated for deletions or mutations, plus nuclear DNA for selected genes (particularly those encoding ETC components).</p>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 mb-2">Functional Tests</h3>
              <p>Cardiopulmonary exercise testing can reveal real-time exercise intolerance, measuring maximal oxygen consumption (VO2 max) and the threshold at which lactate accumulates.</p>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 mb-2">Imaging Techniques</h3>
              <p>MRI or MRS (magnetic resonance spectroscopy) to observe metabolic shifts in specific tissues.</p>
            </div>
          </div>
          
          <p>A thorough assessment recognizes that mitochondrial health is integral to overall metabolic, endocrine, and immune function. Consequently, evaluating hormones, inflammatory markers, environmental toxin exposures, and nutrient status in tandem provides a more comprehensive patient profile.</p>
        </>
      )
    },
    {
      id: 11,
      title: "Therapeutic Strategies and Clinical Considerations",
      content: (
        <>
          <h3 className="text-xl font-medium mb-3">A. Nutrient Optimization</h3>
          <p>Repleting essential nutrients known to support the ETC, citric acid cycle, and antioxidant systems is a linchpin of mitochondrial therapy. Typical protocols emphasize:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 mb-6">
            <li>High-dose B vitamins (for NADH, FADH2, and coenzyme A formation)</li>
            <li>Magnesium (for ATP utilization, membrane stability) in synergy with B6</li>
            <li>CoQ10 (for electron transport and antioxidant capacity)</li>
            <li>NAC, alpha-lipoic acid, or glutathione (for redox defense)</li>
            <li>Carnitine (for fatty acid transport into mitochondria)</li>
            <li>Tryptophan or broad-spectrum amino acid formulas</li>
          </ul>
          
          <h3 className="text-xl font-medium mb-3">B. Hormonal Balance</h3>
          <p>Thyroid hormones substantially influence mitochondrial function—low T3 states reduce the rate of oxidative phosphorylation and overall ATP synthesis. Ensuring proper adrenal and sex hormone balance is also key, as chronic stress or low sex steroids can disrupt normal functions.</p>
          
          <h3 className="text-xl font-medium mb-3 mt-6">C. Reducing Oxidative Load and Toxin Exposure</h3>
          <p>Minimizing exposure to heavy metals, pesticides, and solvents is a foundational step. Where toxins have accumulated, integrative approaches (nutritional detox, sauna therapy, or the use of binders) can help lighten the body's toxic burden.</p>
          
          <h3 className="text-xl font-medium mb-3 mt-6">D. Enhancing Mitochondrial Biogenesis</h3>
          <p>Strategies that activate AMPK and PGC1-alpha spur fresh mitochondrial generation and help phase out dysfunctional organelles. Exercise, intermittent fasting, mild cold exposure, and certain nutraceuticals (e.g., resveratrol) can all encourage new mitochondria to form.</p>
          
          <KeyTakeaway>
            A comprehensive mitochondrial health strategy addresses both upstream factors (toxins, diet, stress) and direct mitochondrial support (nutrients, antioxidants) while optimizing cellular signaling pathways for maximum energy production.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 12,
      title: "Additional Clinical Considerations",
      content: (
        <>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-medium mb-3">E. Regulating mTOR and Sirtuins</h3>
              <p>Balancing mTOR (e.g., through dietary protein moderation, time-restricted eating) and supporting sirtuin function (e.g., via boosting NAD+ levels with nicotinamide riboside or nicotinamide mononucleotide) can properly calibrate anabolic and catabolic pathways. Coordinated regulation ensures that cells neither remain in a constant growth state nor in chronic starvation mode, optimizing both repair and longevity.</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">F. Sequencing Interventions</h3>
              <p>Addressing mitochondrial dysfunction first can be vital when tackling complex cases (e.g., thyroid issues that won't resolve, heavy metal burdens, or persistent infections). Adequate mitochondrial energy is needed for detoxification, immune responses, and gut barrier healing. By improving ATP generation, patients can better tolerate subsequent protocols for gut repair, heavy metal chelation, or other targeted therapies.</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">G. Addressing Underlying Medical Conditions</h3>
              <p>In some cases, identifying an undiagnosed infection (e.g., viral, bacterial overgrowth, or parasitic) or autoimmune condition is crucial, as persistent inflammation can sabotage mitochondrial repair. Patients with unresolved metabolic or endocrine issues must be assessed for insulin resistance, subclinical hypothyroidism, or chronic stress. By treating root conditions, the environment becomes more conducive for mitochondria to regain function.</p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 13,
      title: "Putting it All Together",
      content: (
        <>
          <p>Mitochondrial health is not a stand-alone concept—it forms the bedrock of healthy physiology, bridging metabolic pathways, hormonal signaling, and immune function. When approaching mitochondrial dysfunction clinically or personally, consider these multifaceted steps:</p>
          
          <div className="space-y-4 my-6">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r-md">
              <h3 className="font-medium text-blue-900 mb-1">1. Return to Basics</h3>
              <p>Ensure fundamental nutritional building blocks and an antioxidant-rich diet (including adequate dietary fat).</p>
            </div>
            
            <div className="bg-indigo-50 border-l-4 border-indigo-500 p-3 rounded-r-md">
              <h3 className="font-medium text-indigo-900 mb-1">2. Evaluate Lifestyle</h3>
              <p>Embrace regular exercise, adequate sleep, stress-reducing practices, and supportive relationships that encourage natural mitochondrial turnover.</p>
            </div>
            
            <div className="bg-purple-50 border-l-4 border-purple-500 p-3 rounded-r-md">
              <h3 className="font-medium text-purple-900 mb-1">3. Targeted Supplementation</h3>
              <p>Incorporate CoQ10, magnesium (plus B6), NAC, B vitamins, carnitine, and in some cases specialized amino acid formulas. Where necessary, assess and correct glutathione depletion.</p>
            </div>
            
            <div className="bg-rose-50 border-l-4 border-rose-500 p-3 rounded-r-md">
              <h3 className="font-medium text-rose-900 mb-1">4. Fine-Tune Signaling Pathways</h3>
              <p>Use dietary strategies to balance AMPK, mTOR, sirtuins, and PGC1-alpha. Intermittent fasting can be a powerful method to reset metabolic pathways, while carefully selected amino-acid supplementation can fine-tune the anabolic-catabolic balance.</p>
            </div>
            
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-md">
              <h3 className="font-medium text-amber-900 mb-1">5. Investigate Toxins and Infections</h3>
              <p>Remove or reduce environmental toxicant exposures and treat chronic or hidden infections that keep immune responses high.</p>
            </div>
            
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-3 rounded-r-md">
              <h3 className="font-medium text-emerald-900 mb-1">6. Support Healing and Regeneration</h3>
              <p>Adequate protein, essential fatty acids, and key micronutrients sustain tissue repair and foster robust mitochondrial networks.</p>
            </div>
          </div>
          
          <p>Within a broader framework of functional medicine, clinicians or individuals can track improvements using physical metrics (such as energy levels, endurance, muscle strength) and biochemical parameters (organic acids tests, RBC magnesium, glutathione levels, lactate/pyruvate ratios, and advanced imaging). Over time, consistent application of these strategies can result in increased resilience, less fatigue, and lower risk for the myriad of diseases linked to mitochondrial dysfunction.</p>
        </>
      )
    },
    {
      id: 14,
      title: "Conclusion",
      content: (
        <>
          <p>Mitochondria stand at the nexus of energy metabolism, cellular signaling, and overall innate vitality. Their well-being hinges on proper nutrition, the avoidance of insults like toxins and excessive oxidative stress, and the harmonious regulation of such pathways as AMPK, mTOR, and sirtuins.</p>
          
          <p>Various disease states, from the persistent fatigue of fibromyalgia to cardiovascular disease, diabetes, neurodegenerative disorders, and cancer, share an underlying thread of mitochondrial dysfunction. Yet a growing compendium of evidence supports the conclusion that by modulating key pathways—through exercise, nutrient replenishment, intermittent fasting, or pharmacological agents—we can restore or at least partially rescue mitochondrial function.</p>
          
          <p>For practitioners, evaluating mitochondrial function provides a foundational lens through which to assess chronic disease and complex syndromes. Interventions such as measuring plasma lactate or performing organic acids testing help pinpoint metabolic bottlenecks, while supplementation with NAC, CoQ10, magnesium, and B vitamins can help patients climb out of the fatigue and inflammation spiral.</p>
          
          <KeyTakeaway>
            Ultimately, caring for mitochondria translates to investing in long-term health. A lifestyle rich in nutrient-dense foods, consistent physical activity, stress management, and mindful sleep hygiene fosters robust mitochondrial networks that support health and longevity.
          </KeyTakeaway>
        </>
      )
    }
  ];

  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            className="flex items-center text-[#3498DB]"
            onClick={() => navigate("/mentorship")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Mentorship
          </Button>
          
          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            Lecture Material
          </div>
        </div>
        
        <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
          Mitochondrial Health
        </h1>
        <p className="text-[#7F8C8D] mb-6">
          A comprehensive exploration of mitochondrial structure, function, and therapeutic strategies 
          for optimizing cellular energy production and addressing mitochondrial dysfunction.
        </p>
      
        <Slideshow slides={lectureSlides} />
      </div>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}

export default MitochondrialHealth;